import { useEffect, useMemo, useState } from "react";
import { createLinkApiClient, type LinkApiClient } from "./linkApiClient";
import {
  deleteLocalLinkRecord,
  listLocalLinkRecords,
  markLocalLinkRevoked,
  type LocalLinkRecord,
} from "./linkRecords";
import { copyShareLink, formatLinkBytes, formatLinkExpiry } from "./uiUtils";
import "./linkShare.css";

export interface ActiveLinksPanelProps {
  apiClient?: LinkApiClient;
}

interface LinkRecordView extends LocalLinkRecord {
  expired: boolean;
}

export function ActiveLinksPanel({ apiClient }: ActiveLinksPanelProps) {
  const client = useMemo(() => apiClient ?? createLinkApiClient(), [apiClient]);
  const [records, setRecords] = useState<LinkRecordView[]>([]);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    void listLocalLinkRecords()
      .then((values) => {
        if (active) {
          const checkedAt = Date.now();
          setRecords(
            values.map((record) => ({
              ...record,
              expired: checkedAt >= Date.parse(record.expiresAt),
            })),
          );
        }
      })
      .catch(() => {
        if (active) {
          setNotice("Local link records could not be read.");
        }
      });
    return () => {
      active = false;
    };
  }, []);

  async function revoke(record: LinkRecordView): Promise<void> {
    if (
      !window.confirm(
        "Revoke this link? Future downloads stop, but saved copies remain.",
      )
    ) {
      return;
    }
    try {
      await client.revoke(record.accessToken, record.revokeToken);
      const saved = await markLocalLinkRevoked(record.id);
      const updated: LinkRecordView = {
        ...(saved ?? record),
        status: "revoked",
        expired: record.expired,
      };
      setRecords((current) =>
        current.map((value) => (value.id === record.id ? updated : value)),
      );
      setNotice("Link revoked and temporary cloud copy deleted.");
    } catch {
      setNotice("Revoke failed. The capability was retained so you can retry.");
    }
  }

  async function remove(record: LinkRecordView): Promise<void> {
    if (
      record.status === "active" &&
      !window.confirm(
        "Delete only this local record? The cloud link will remain active until expiry unless you revoke it first.",
      )
    ) {
      return;
    }
    try {
      await deleteLocalLinkRecord(record.id);
      setRecords((current) => current.filter((value) => value.id !== record.id));
      setNotice("Local link record deleted.");
    } catch {
      setNotice("The local link record could not be deleted.");
    }
  }

  return (
    <section className="link-panel" aria-labelledby="active-links-title">
      <div className="link-panel__heading">
        <p className="link-panel__eyebrow">Stored only on this device</p>
        <h2 id="active-links-title">Active links</h2>
        <p>
          These records contain private revoke controls. Removing a record does not
          revoke its cloud copy.
        </p>
      </div>

      {records.length === 0 ? (
        <div className="link-empty">
          <h3>No local link records</h3>
          <p>Links created in this browser will appear here.</p>
        </div>
      ) : (
        <ul className="link-records">
          {records.map((record) => {
            const status =
              record.status === "revoked"
                ? "Revoked"
                : record.expired
                  ? "Expired"
                  : "Active";
            return (
              <li key={record.id}>
                <div>
                  <span className="link-status-chip">{status}</span>
                  <h3>{record.displayName}</h3>
                  <p>
                    {formatLinkBytes(record.ciphertextBytes)} - Available until{" "}
                    <time dateTime={record.expiresAt}>
                      {formatLinkExpiry(record.expiresAt)}
                    </time>
                  </p>
                  {record.environment === "local-simulation" && (
                    <small>Local development simulation</small>
                  )}
                </div>
                <div className="link-actions">
                  {record.status === "active" && !record.expired && (
                    <>
                      <button
                        className="link-button link-button--secondary"
                        type="button"
                        onClick={() => {
                          void copyShareLink(record.shareUrl)
                            .then(() => {
                              setNotice("Link copied.");
                            })
                            .catch(() => {
                              setNotice("The link could not be copied.");
                            });
                        }}
                      >
                        Copy link
                      </button>
                      <button
                        className="link-button link-button--danger"
                        type="button"
                        onClick={() => {
                          void revoke(record);
                        }}
                      >
                        Revoke link
                      </button>
                    </>
                  )}
                  <button
                    className="link-button link-button--quiet"
                    type="button"
                    onClick={() => {
                      void remove(record);
                    }}
                  >
                    Delete local record
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {notice && (
        <p className="link-notice" role="status">
          {notice}
        </p>
      )}
    </section>
  );
}
